import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TGetCompanyUserListResDto } from "@/api/services/main/company-user/dtos/get-company-user-list.dto";
import { TCreateCompanyUserReqDto, TCreateCompanyUserResDto } from "@/api/services/main/company-user/dtos/create-company-user.dto";
import { TUpdateCompanyUserReqDto, TUpdateCompanyUserResDto } from "@/api/services/main/company-user/dtos/update-company-user.dto";
import { TResendCompanyInvitationResDto } from "@/api/services/main/company-user/dtos/resend-invitation.dto";
import { TResendResetPasswordResDto } from "@/api/services/main/company-user/dtos/resend-reset-password.dto";
import { TApiRes } from "@/types/api";
import { TCompanyUser } from "@/api/entities/company-user.entity";
import { useMemo } from "react";

export const fetchCompanyUserListApi = async (companyId: string | number): Promise<TGetCompanyUserListResDto> => {
  const { data } = await clientApi.get<TGetCompanyUserListResDto>(`/company/${companyId}/users`);
  return data;
};

export const useCompanyUserList = (companyId?: string | number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST, companyId],
    queryFn: () => fetchCompanyUserListApi(companyId!),
    enabled: !!companyId,
  });
};

export const bulkDisableCompanyUserApi = async (companyId: string | number, userIds: number[]) => {
  const { data } = await clientApi.patch(`/company/${companyId}/users/bulk-disable`, { ids: userIds });
  return data;
};

export const deleteCompanyUserApi = async (companyId: string | number, userId: string | number) => {
  const { data } = await clientApi.delete(`/company/${companyId}/user/${userId}`);
  return data;
};

export const createCompanyUserApi = async (companyId: string | number, payload: TCreateCompanyUserReqDto): Promise<TCreateCompanyUserResDto> => {
  const { data } = await clientApi.post<TCreateCompanyUserResDto>(`/company/${companyId}/user`, payload);
  return data;
};

export const fetchCompanyUserApi = async (companyId: string | number, userId: string | number): Promise<TApiRes<TCompanyUser>> => {
  const { data } = await clientApi.get<TApiRes<TCompanyUser>>(`/company/${companyId}/user/${userId}`);
  return data;
};

export const useCompanyUserInfo = (companyId?: string | number, userId?: string | number) => {
  const isValidId = useMemo(() => {
    if (!companyId || !userId) return false;
    const cId = Number(companyId);
    const uId = Number(userId);
    return !isNaN(cId) && Number.isInteger(cId) && cId > 0 && !isNaN(uId) && Number.isInteger(uId) && uId > 0;
  }, [companyId, userId]);

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_DETAIL, companyId, userId],
    queryFn: () => fetchCompanyUserApi(companyId!, userId!),
    enabled: isValidId,
  });
};

export const updateCompanyUserApi = async (
  companyId: string | number,
  userId: string | number,
  payload: TUpdateCompanyUserReqDto,
): Promise<TUpdateCompanyUserResDto> => {
  const { data } = await clientApi.put<TUpdateCompanyUserResDto>(`/company/${companyId}/user/${userId}`, payload);
  return data;
};

export const resendCompanyInvitationApi = async (companyId: string, userId: string): Promise<TResendCompanyInvitationResDto> => {
  const rs = await clientApi.post<TResendCompanyInvitationResDto>(`/company/${companyId}/user/${userId}/resend-invitation`);
  return rs.data;
};

export const resendResetPasswordApi = async (companyId: string, userId: string): Promise<TResendResetPasswordResDto> => {
  const rs = await clientApi.post<TResendResetPasswordResDto>(`/company/${companyId}/user/${userId}/resend-reset-password`);
  return rs.data;
};
